import { warnIfScriptOriginMismatch } from "./config"
import { whenDomReady } from "./mount/whenDomReady"
import type { BppSearchConfig, BppSearchGlobal, EmbedUi, SearchMode } from "./types"
import { EmbedController } from "./widget/EmbedController"

let activeController: EmbedController | null = null
let initialized = false

export function initBppSearch(config: BppSearchConfig): void {
  if (initialized) {
    console.warn("[BppSearch] init() called more than once — ignoring.")
    return
  }

  warnIfScriptOriginMismatch(config.searchBaseUrl)
  activeController = new EmbedController(config)
  initialized = true

  whenDomReady(() => {
    void activeController?.mount()
  })
}

export function destroyBppSearch(): void {
  activeController?.destroy()
  activeController = null
  initialized = false
}

export const BppSearch: BppSearchGlobal = {
  init: initBppSearch,
  destroy: destroyBppSearch,
}

function parseSearchMode(raw: string | undefined): SearchMode {
  if (raw === "agent") {
    return "agent"
  }
  if (raw === "office") {
    return "office"
  }
  return "property"
}

function parseUi(raw: string | undefined): EmbedUi | undefined {
  return raw === "dropdown" || raw === "lightbox" ? raw : undefined
}

function parseDataAttributes(script: HTMLScriptElement): BppSearchConfig | null {
  if (!script.dataset.bppSearch && !script.dataset.searchBaseUrl) {
    return null
  }

  const searchBaseUrl = script.dataset.searchBaseUrl
  if (!searchBaseUrl) {
    console.error("[BppSearch] data-search-base-url is required")
    return null
  }

  return {
    searchBaseUrl,
    ctaPresentation:
      script.dataset.ctaPresentation === "embedded" ? "embedded" : "external",
    mount: script.dataset.mount,
    mode: parseSearchMode(script.dataset.mode),
    ui: parseUi(script.dataset.ui),
    openInNewTab: script.dataset.openInNewTab === "true",
    triggers: script.dataset.trigger
      ? [
          {
            selector: script.dataset.trigger,
            mode: parseSearchMode(script.dataset.mode),
            defaultTab:
              script.dataset.defaultTab === "rent"
                ? "rent"
                : script.dataset.defaultTab === "commercial"
                  ? "commercial"
                  : "buy",
          },
        ]
      : undefined,
  }
}

export function autoInitFromScriptTag(): void {
  const script = document.currentScript
  if (!(script instanceof HTMLScriptElement)) {
    return
  }
  const config = parseDataAttributes(script)
  if (config) {
    initBppSearch(config)
  }
}
